import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Scan, ShieldCheck, BarChart3, Smartphone, Lock, Ticket, UserCheck, Crown, Play } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_brandedticket/artifacts/xh5dnoz3_Screenshot_20260403_164953_Gallery.jpg";
const HAS_SEEN_KEY = 'vg_intro_seen';

function GateAnimation({ onComplete }) {
  const [phase, setPhase] = useState('closed');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('opening'), 400);
    const t2 = setTimeout(() => setPhase('open'), 1600);
    const t3 = setTimeout(() => onComplete(), 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 bg-[#050505] flex items-center justify-center overflow-hidden">
      {/* Left door */}
      <div
        className="absolute top-0 left-0 h-full w-1/2 bg-[#0A0A0A] border-r border-[#C6A66B]/20 transition-transform duration-[1000ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{ transform: phase === 'closed' ? 'translateX(0)' : 'translateX(-100%)' }}
      >
        <div className="absolute right-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-[#C6A66B]/40 to-transparent" />
        <div className="absolute right-6 top-1/2 -translate-y-1/2 w-1 h-24 rounded-full bg-[#C6A66B]/30" />
      </div>
      {/* Right door */}
      <div
        className="absolute top-0 right-0 h-full w-1/2 bg-[#0A0A0A] border-l border-[#C6A66B]/20 transition-transform duration-[1000ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{ transform: phase === 'closed' ? 'translateX(0)' : 'translateX(100%)' }}
      >
        <div className="absolute left-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-[#C6A66B]/40 to-transparent" />
        <div className="absolute left-6 top-1/2 -translate-y-1/2 w-1 h-24 rounded-full bg-[#C6A66B]/30" />
      </div>
      {/* Center mark */}
      <div className={`relative z-10 transition-opacity duration-500 ${phase === 'closed' ? 'opacity-100' : 'opacity-0'}`}>
        <img src={LOGO_URL} alt="VelvetGate" className="h-12 mx-auto" />
      </div>
    </div>
  );
}

function FadeInSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function MockCheckout() {
  return (
    <div className="bg-[#0A0A0A] rounded-sm overflow-hidden border border-[#1A1A1A] mx-auto" style={{ width: '100%', maxWidth: 260 }}>
      <div className="px-4 pt-4 pb-2">
        <div className="text-[8px] tracking-[2px] text-[#C6A66B] uppercase mb-1">Select Pass</div>
        <div className="text-[11px] font-bold text-[#EAE7E1]">Friday Night Social</div>
      </div>
      <div className="px-4 space-y-2 pb-3">
        <div className="flex items-center justify-between px-3 py-2 border border-[#C6A66B]/40 bg-[#C6A66B]/5 rounded-sm">
          <div><div className="text-[10px] font-semibold text-[#EAE7E1]">General</div><div className="text-[8px] text-[#777]">Standard entry</div></div>
          <div className="text-[10px] font-bold text-[#C6A66B]">$25</div>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border border-[#1A1A1A] rounded-sm">
          <div><div className="text-[10px] font-semibold text-[#AFAFAF]">VIP</div><div className="text-[8px] text-[#555]">Priority entry + drink</div></div>
          <div className="text-[10px] text-[#777]">$50</div>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border border-[#1A1A1A] rounded-sm">
          <div><div className="text-[10px] font-semibold text-[#AFAFAF]">Couples</div><div className="text-[8px] text-[#555]">2 passes + perks</div></div>
          <div className="text-[10px] text-[#777]">$80</div>
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className="w-full py-2 bg-[#D4AF37] text-[#050505] text-center text-[10px] font-bold rounded-sm">Continue</div>
      </div>
    </div>
  );
}

function MockPass() {
  return (
    <div className="bg-[#0A0A0A] rounded-sm overflow-hidden border border-[#1A1A1A] mx-auto" style={{ width: '100%', maxWidth: 260 }}>
      <div className="bg-gradient-to-b from-[#C6A66B]/10 to-transparent px-4 pt-4 pb-2 text-center">
        <div className="text-[8px] tracking-[2px] text-[#C6A66B] uppercase">VelvetGate Pass</div>
        <div className="text-[12px] font-bold text-[#EAE7E1] mt-1">Friday Night Social</div>
        <div className="text-[9px] text-[#777] mt-0.5">General Admission</div>
      </div>
      <div className="flex justify-center py-4">
        <div className="w-28 h-28 bg-white rounded-sm p-2">
          <div className="w-full h-full" style={{ background: 'repeating-conic-gradient(#000 0% 25%, #fff 0% 50%) 50%/6px 6px' }} />
        </div>
      </div>
      <div className="px-4 pb-4 text-center">
        <div className="text-[9px] text-[#777]">Alex Rivera</div>
        <div className="text-[8px] text-[#555] mt-0.5">Friday 06-20-2026 09:00 PM</div>
        <div className="mt-2 w-full py-1.5 border border-[#C6A66B]/30 text-[#C6A66B] text-center text-[9px] font-medium rounded-sm">Add to Wallet</div>
      </div>
    </div>
  );
}

function MockScanner() {
  return (
    <div className="bg-[#0A0A0A] rounded-sm overflow-hidden border border-[#1A1A1A] mx-auto" style={{ width: '100%', maxWidth: 260 }}>
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <div className="text-[11px] font-bold text-[#EAE7E1]">Check-In Scanner</div>
        <div className="text-[9px] text-[#6EE7B7] font-semibold">12/50</div>
      </div>
      <div className="mx-4 mb-3 aspect-[4/3] bg-[#111] rounded-sm flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-4 border-2 border-[#C6A66B]/50 rounded-sm" />
        <div className="absolute left-4 right-4 top-1/2 h-0.5 bg-[#D4AF37]/60 animate-pulse" />
        <Scan className="w-8 h-8 text-[#C6A66B]/30" />
      </div>
      <div className="px-4 pb-3 space-y-1.5">
        <div className="flex items-center gap-2 px-3 py-2 bg-[#6EE7B7]/10 border border-[#6EE7B7]/20 rounded-sm">
          <div className="w-4 h-4 rounded-full bg-[#6EE7B7]/20 flex items-center justify-center"><ShieldCheck className="w-2.5 h-2.5 text-[#6EE7B7]" /></div>
          <div><div className="text-[9px] font-semibold text-[#6EE7B7]">Confirmed</div><div className="text-[8px] text-[#777]">Alex Rivera — General</div></div>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-[#6EE7B7]/5 border border-[#1A1A1A] rounded-sm">
          <div className="w-4 h-4 rounded-full bg-[#6EE7B7]/10 flex items-center justify-center"><ShieldCheck className="w-2.5 h-2.5 text-[#6EE7B7]/50" /></div>
          <div><div className="text-[9px] text-[#AFAFAF]">Maria Santos — VIP</div><div className="text-[8px] text-[#555]">10:02 PM</div></div>
        </div>
      </div>
    </div>
  );
}

function MockDashboard() {
  return (
    <div className="bg-[#0A0A0A] rounded-sm overflow-hidden border border-[#1A1A1A] mx-auto" style={{ width: '100%', maxWidth: 260 }}>
      <div className="px-4 pt-4 pb-2">
        <div className="text-[11px] font-bold text-[#EAE7E1]">Manage Event</div>
        <div className="text-[8px] text-[#777]">Friday Night Social</div>
      </div>
      <div className="px-4 pb-3 grid grid-cols-3 gap-1.5">
        {[
          { label: 'Sold', val: '47', c: 'text-[#EAE7E1]' },
          { label: 'Checked In', val: '12', c: 'text-[#6EE7B7]' },
          { label: 'Revenue', val: '$1,850', c: 'text-[#D4AF37]' },
        ].map(s => (
          <div key={s.label} className="bg-[#111] rounded-sm p-2 text-center">
            <div className={`text-[11px] font-bold ${s.c}`}>{s.val}</div>
            <div className="text-[7px] text-[#555] uppercase">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="px-4 pb-3 space-y-1.5">
        <div className="flex items-center justify-between px-3 py-2 bg-[#111] rounded-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-[#C6A66B]/10 flex items-center justify-center"><Smartphone className="w-2.5 h-2.5 text-[#C6A66B]" /></div>
            <div className="text-[9px] text-[#EAE7E1]">Message Guests</div>
          </div>
          <ArrowRight className="w-3 h-3 text-[#555]" />
        </div>
        <div className="flex items-center justify-between px-3 py-2 bg-[#111] rounded-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-[#C6A66B]/10 flex items-center justify-center"><ShieldCheck className="w-2.5 h-2.5 text-[#C6A66B]" /></div>
            <div className="text-[9px] text-[#EAE7E1]">Staff Management</div>
          </div>
          <ArrowRight className="w-3 h-3 text-[#555]" />
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className="w-full py-2 bg-[#D4AF37] text-[#050505] text-center text-[10px] font-bold rounded-sm">Open Scanner</div>
      </div>
    </div>
  );
}

const WALKTHROUGH_STEPS = [
  { num: '01', label: 'Guest Checkout', caption: 'Guests choose a pass and check out in seconds', Mock: MockCheckout, link: '/demo', cta: 'Try Guest Experience' },
  { num: '02', label: 'Instant Pass', caption: 'Every guest gets a scannable QR pass instantly', Mock: MockPass, link: '/demo' },
  { num: '03', label: 'Live Check-In', caption: 'Scan or search — entry confirmed instantly', Mock: MockScanner },
  { num: '04', label: 'Host Control', caption: 'Manage guests, staff, and access in real time', Mock: MockDashboard },
];

function WalkthroughCarousel() {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const cardW = el.firstChild?.offsetWidth || 300;
    const idx = Math.round(el.scrollLeft / cardW);
    setActive(Math.min(idx, WALKTHROUGH_STEPS.length - 1));
  };

  const scrollTo = (idx) => {
    const el = scrollRef.current;
    if (!el || !el.children[idx]) return;
    el.children[idx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  return (
    <div>
      {/* Scroll container */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none px-4 sm:px-8 pb-6"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        data-testid="walkthrough-scroll"
      >
        {WALKTHROUGH_STEPS.map((step, i) => {
          const isLinked = !!step.link;
          const Wrapper = isLinked ? 'button' : 'div';
          return (
            <div
              key={step.num}
              className="snap-center flex-shrink-0 w-[85vw] sm:w-[340px] lg:w-[320px]"
              data-testid={`walkthrough-card-${i}`}
            >
              <Wrapper
                onClick={isLinked ? () => navigate(step.link) : undefined}
                className={`bg-gradient-to-b from-[#0A0A0A] to-[#050505] border border-[#1A1A1A] rounded-sm overflow-hidden h-full flex flex-col w-full text-left transition-all duration-200 ${isLinked ? 'cursor-pointer hover:border-[#C6A66B]/40 active:scale-[0.97] active:opacity-90' : ''}`}
                data-testid={isLinked ? `walkthrough-link-${i}` : undefined}
              >
                {/* Step indicator + CTA badge */}
                <div className="flex items-center justify-between px-5 pt-5 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-black text-[#C6A66B]/25">{step.num}</span>
                    <span className="text-xs font-bold text-[#C6A66B] uppercase tracking-wider">{step.label}</span>
                  </div>
                  {step.cta && (
                    <span className="text-[9px] font-semibold text-[#C6A66B] uppercase tracking-wider px-2 py-0.5 border border-[#C6A66B]/25 rounded-full animate-pulse" data-testid="try-live-badge">
                      {step.cta}
                    </span>
                  )}
                </div>
                {/* Mock screen */}
                <div className="flex-1 flex items-center justify-center px-4 py-4 bg-[#080808]">
                  <step.Mock />
                </div>
                {/* Caption */}
                <div className="px-5 py-4">
                  <p className="text-sm text-[#B9B1A6] leading-relaxed">{step.caption}</p>
                </div>
              </Wrapper>
            </div>
          );
        })}
      </div>

      {/* Dots */}
      <div className="flex items-center justify-center gap-2 pt-2" data-testid="walkthrough-dots">
        {WALKTHROUGH_STEPS.map((step, i) => (
          <button
            key={step.num}
            onClick={() => scrollTo(i)}
            className={`rounded-full transition-all duration-300 ${active === i ? 'w-6 h-1.5 bg-[#C6A66B]' : 'w-1.5 h-1.5 bg-[#333] hover:bg-[#555]'}`}
            data-testid={`walkthrough-dot-${i}`}
            aria-label={`Go to step ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [showGate, setShowGate] = useState(false);
  const [gateComplete, setGateComplete] = useState(false);
  const [showDemoFab, setShowDemoFab] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    if (!sessionStorage.getItem(HAS_SEEN_KEY)) {
      setShowGate(true);
      sessionStorage.setItem(HAS_SEEN_KEY, '1');
    } else {
      setGateComplete(true);
    }
  }, []);

  useEffect(() => {
    const check = () => {
      const el = previewRef.current;
      if (!el) return;
      setShowDemoFab(el.getBoundingClientRect().bottom < window.innerHeight * 0.3);
    };
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, []);

  const handleGateComplete = () => {
    setShowGate(false);
    setGateComplete(true);
  };

  const goCreate = () => navigate('/login');
  const goDemo = () => navigate('/demo');

  if (!gateComplete && !showGate) return null;

  return (
    <>
      {showGate && <GateAnimation onComplete={handleGateComplete} />}

      <div className={`min-h-screen bg-[#050505] ${gateComplete ? 'opacity-100' : 'opacity-0'} transition-opacity duration-700`}>

        {/* ── Nav ── */}
        <nav className="fixed top-0 left-0 right-0 z-40 bg-[#050505]/80 backdrop-blur-md border-b border-[#1A1A1A]/60">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
            <img src={LOGO_URL} alt="VelvetGate" className="h-8" data-testid="nav-logo" />
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/login')} className="text-sm text-[#B9B1A6] hover:text-[#F3EEE7] font-medium transition-colors" data-testid="nav-login">Sign In</button>
              <button onClick={goCreate} className="text-sm bg-[#C6A66B] hover:bg-[#D4AF37] text-[#050505] font-bold px-4 py-2 rounded-sm transition-colors" data-testid="nav-cta">Get Started</button>
            </div>
          </div>
        </nav>

        {/* ── Hero ── */}
        <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 px-4" data-testid="hero-section">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(198,166,107,0.06)_0%,transparent_60%)]" />
          <div className="relative max-w-3xl mx-auto text-center">
            <FadeInSection>
              <div className="text-[10px] tracking-[0.4em] text-[#C6A66B] font-semibold uppercase mb-6">Event Access Platform</div>
            </FadeInSection>
            <FadeInSection delay={100}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#F3EEE7] tracking-tight leading-[1.1] mb-6" data-testid="hero-headline">
                Control the door.<br />Own the night.
              </h1>
            </FadeInSection>
            <FadeInSection delay={200}>
              <p className="text-base sm:text-lg text-[#B9B1A6] max-w-lg mx-auto mb-3 leading-relaxed" data-testid="hero-subheadline">
                Buy passes, run your door, host approved events — all in one place.
              </p>
            </FadeInSection>
            <FadeInSection delay={250}>
              <p className="text-xs text-[#777] max-w-md mx-auto mb-10" data-testid="hero-sub-note">
                Free to join. Hosting requires a short application and approval.
              </p>
            </FadeInSection>
            <FadeInSection delay={300}>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button onClick={goCreate} className="w-full sm:w-auto bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F] hover:opacity-90 text-[#050505] font-bold px-8 py-3.5 rounded-sm text-sm flex items-center justify-center gap-2 transition-opacity" data-testid="hero-cta-primary">
                  Create Account <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={goDemo} className="w-full sm:w-auto border border-[#C6A66B]/30 text-[#C6A66B] hover:bg-[#C6A66B]/5 font-semibold px-8 py-3.5 rounded-sm text-sm transition-colors" data-testid="hero-cta-secondary">
                  Try Guest Experience
                </button>
              </div>
            </FadeInSection>
          </div>
        </section>

        {/* ── Transition 1 ── */}
        <div className="py-12 px-4 text-center">
          <FadeInSection>
            <div className="max-w-md mx-auto">
              <div className="h-px bg-gradient-to-r from-transparent via-[#C6A66B]/25 to-transparent mb-8" />
              <p className="text-sm text-[#777] tracking-wide" data-testid="transition-1">Everything you need — from setup to check-in.</p>
            </div>
          </FadeInSection>
        </div>

        {/* ── How It Works ── */}
        <section className="py-16 sm:py-20 px-4" data-testid="how-it-works-section">
          <div className="max-w-4xl mx-auto">
            <FadeInSection>
              <div className="text-center mb-14">
                <div className="text-[10px] tracking-[0.35em] text-[#C6A66B] font-semibold uppercase mb-3">How It Works</div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F3EEE7] tracking-tight">Three steps to a sold-out event</h2>
              </div>
            </FadeInSection>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {[
                { num: '01', title: 'Create & Configure', desc: 'Set up your event, define pass tiers, pricing, and payment methods in minutes.', icon: Zap },
                { num: '02', title: 'Share & Collect', desc: 'Share your event link. Guests select passes, pay directly, and receive their VelvetGate Pass.', icon: Smartphone },
                { num: '03', title: 'Scan & Check In', desc: 'Use the built-in scanner at the door. One tap check-in with full audit trail.', icon: Scan }
              ].map((step, i) => (
                <FadeInSection key={step.num} delay={i * 120}>
                  <div className="relative bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6 sm:p-8 hover:border-[#C6A66B]/30 transition-colors group" data-testid={`step-${step.num}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-2xl font-black text-[#C6A66B]/20 group-hover:text-[#C6A66B]/40 transition-colors">{step.num}</span>
                      <step.icon className="w-5 h-5 text-[#C6A66B]" />
                    </div>
                    <h3 className="text-base font-bold text-[#F3EEE7] mb-2">{step.title}</h3>
                    <p className="text-sm text-[#B9B1A6] leading-relaxed">{step.desc}</p>
                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>
        </section>

        {/* ── Transition 2 ── */}
        <div className="py-12 px-4 text-center">
          <FadeInSection>
            <div className="max-w-md mx-auto">
              <div className="h-px bg-gradient-to-r from-transparent via-[#C6A66B]/25 to-transparent mb-8" />
              <p className="text-sm text-[#777] tracking-wide" data-testid="transition-2">Not all event platforms are built like this.</p>
            </div>
          </FadeInSection>
        </div>

        {/* ── Why VelvetGate ── */}
        <section className="py-16 sm:py-20 px-4" data-testid="why-section">
          <div className="max-w-4xl mx-auto">
            <FadeInSection>
              <div className="text-center mb-14">
                <div className="text-[10px] tracking-[0.35em] text-[#C6A66B] font-semibold uppercase mb-3">Why VelvetGate</div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F3EEE7] tracking-tight">Built for hosts who care about the details</h2>
              </div>
            </FadeInSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { title: 'Direct Payments', desc: 'Guests pay you directly via Zelle, Venmo, or any method. No middleman fees. No platform take.', icon: ShieldCheck },
                { title: 'Real-Time Check-In', desc: 'Barcode scanning, manual override, full audit trail. Know exactly who walked in — and when.', icon: Scan },
                { title: 'Flexible Pass Tiers', desc: 'General, VIP, Couples, Comp — with quantity controls, access codes, and add-on stacking.', icon: BarChart3 },
                { title: 'Premium Guest Experience', desc: 'Dark luxury checkout, branded PDF passes with QR codes, and mobile-first confirmation pages.', icon: Lock }
              ].map((card, i) => (
                <FadeInSection key={card.title} delay={i * 100}>
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6 hover:border-[#C6A66B]/25 transition-colors" data-testid={`why-card-${i}`}>
                    <div className="w-9 h-9 rounded-sm bg-[#C6A66B]/8 flex items-center justify-center mb-4">
                      <card.icon className="w-4.5 h-4.5 text-[#C6A66B]" />
                    </div>
                    <h3 className="text-base font-bold text-[#F3EEE7] mb-2">{card.title}</h3>
                    <p className="text-sm text-[#B9B1A6] leading-relaxed">{card.desc}</p>
                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>
        </section>

        {/* ── See It In Action ── */}
        <section id="preview" ref={previewRef} className="py-16 sm:py-20 bg-[#0A0A0A]/50 overflow-hidden" data-testid="preview-section">
          <div className="px-4">
            <FadeInSection>
              <div className="text-center mb-10">
                <div className="text-[10px] tracking-[0.35em] text-[#C6A66B] font-semibold uppercase mb-3">See It In Action</div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F3EEE7] tracking-tight">Your event. Your brand. Your control.</h2>
              </div>
            </FadeInSection>
          </div>
          <FadeInSection delay={150}>
            <WalkthroughCarousel />
          </FadeInSection>
        </section>

        {/* ── Built for Every Role ── */}
        <section className="py-16 sm:py-20 px-4" data-testid="roles-section">
          <div className="max-w-3xl mx-auto">
            <FadeInSection>
              <div className="text-center mb-6">
                <div className="text-[10px] tracking-[0.35em] text-[#C6A66B] font-semibold uppercase mb-3">How Roles Work</div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F3EEE7] tracking-tight">Four roles. One clear path.</h2>
              </div>
            </FadeInSection>
            <FadeInSection delay={80}>
              <p className="text-sm text-[#B9B1A6] text-center max-w-xl mx-auto mb-10" data-testid="roles-progression">
                <span className="text-[#F3EEE7] font-semibold">Guest</span> <span className="text-[#555]">→</span> <span className="text-[#F3EEE7] font-semibold">User Account</span> <span className="text-[#555]">→</span> <span className="text-[#F3EEE7] font-semibold">Host Application</span> <span className="text-[#555]">→</span> <span className="text-[#D4AF37] font-semibold">Approved Host</span>
                <br/>
                <span className="text-[10px] tracking-[2px] text-[#777] uppercase mt-2 inline-block">Staff = invited by a host (check-in only)</span>
              </p>
            </FadeInSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { icon: Ticket, title: 'Guest', desc: 'Browse the demo or buy a pass as a visitor. No account required to view events.', how: 'No signup needed' },
                { icon: UserCheck, title: 'User', desc: 'Create a free account to buy passes, view “My Passes,” and manage your profile. Cannot create events.', how: 'Sign up — instant' },
                { icon: Crown, title: 'Host', desc: 'Create and run your own events, collect payments, and manage guests. Requires an application and approval.', how: 'Apply from your account' },
                { icon: Scan, title: 'Staff', desc: 'Scan and check guests in at the door. Invited by a host — no application needed.', how: 'Invitation only' },
              ].map((role, i) => (
                <FadeInSection key={role.title} delay={i * 100}>
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6 hover:border-[#C6A66B]/20 transition-colors" data-testid={`role-card-${i}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-[#C6A66B]/8 flex items-center justify-center flex-shrink-0">
                        <role.icon className="w-5 h-5 text-[#C6A66B]" />
                      </div>
                      <h3 className="text-base font-bold text-[#F3EEE7]">{role.title}</h3>
                    </div>
                    <p className="text-sm text-[#B9B1A6] leading-relaxed mb-3">{role.desc}</p>
                    <div className="text-[10px] tracking-[1.5px] text-[#C6A66B]/80 uppercase font-semibold" data-testid={`role-how-${i}`}>{role.how}</div>
                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>
        </section>

        {/* ── Built for every level of host (Plans overview — no pricing) ── */}
        <section className="py-16 sm:py-20 px-4 border-t border-[#1A1A1A]" data-testid="plans-section">
          <FadeInSection>
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <div className="text-[10px] tracking-[0.35em] text-[#C6A66B] font-semibold uppercase mb-3">Plans</div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#F3EEE7] tracking-tight" data-testid="plans-headline">
                  Built for every level of host
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plans-grid">
                <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-5" data-testid="plan-card-starter">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-2">Starter</div>
                  <h3 className="text-lg font-bold text-[#F3EEE7] mb-2">Run your event</h3>
                  <p className="text-sm text-[#B9B1A6] leading-relaxed">Create, approve, and check in. Everything you need to launch your first night without friction.</p>
                </div>
                <div className="bg-[#0A0A0A] border border-[#D4AF37]/25 rounded-sm p-5" data-testid="plan-card-pro">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-2">Pro</div>
                  <h3 className="text-lg font-bold text-[#F3EEE7] mb-2">Grow with insights</h3>
                  <p className="text-sm text-[#B9B1A6] leading-relaxed">Unlock advanced reports, Post Event Insights, saved snapshots, access codes, and discount codes.</p>
                </div>
                <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-5" data-testid="plan-card-premium">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-2">Premium</div>
                  <h3 className="text-lg font-bold text-[#F3EEE7] mb-2">Own your brand</h3>
                  <p className="text-sm text-[#B9B1A6] leading-relaxed">Custom branding on tickets, emails, and event pages. Your identity, end-to-end.</p>
                </div>
              </div>
              <div className="text-center mt-6">
                <a href="/guide#plans-access" className="text-xs text-[#D4AF37] hover:underline" data-testid="plans-learn-more">
                  Learn what each plan unlocks →
                </a>
              </div>
            </div>
          </FadeInSection>
        </section>

        {/* ── Final CTA ── */}
        <section className="py-20 sm:py-28 px-4" data-testid="final-cta-section">
          <FadeInSection>
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#F3EEE7] tracking-tight mb-4" data-testid="final-headline">
                Run your next event the right way.
              </h2>
              <p className="text-sm sm:text-base text-[#B9B1A6] mb-2">No complicated setup. Be live in minutes once your host application is approved.</p>
              <p className="text-xs text-[#777] mb-10">Start by creating an account — apply to host from your dashboard.</p>
              <button onClick={goCreate} className="bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F] hover:opacity-90 text-[#050505] font-bold px-10 py-4 rounded-sm text-sm flex items-center justify-center gap-2 mx-auto transition-opacity" data-testid="final-cta-button">
                Create Account <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </FadeInSection>
        </section>

        {/* ── Footer ── */}
        <footer className="border-t border-[#1A1A1A] py-8 px-4">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <img src={LOGO_URL} alt="VelvetGate" className="h-7 opacity-60" />
            <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-[#777]" aria-label="Footer navigation" data-testid="landing-footer-nav">
              <a href="/faq"     className="hover:text-[#D4AF37] transition-colors" data-testid="landing-footer-faq">FAQ</a>
              <a href="/contact" className="hover:text-[#D4AF37] transition-colors" data-testid="landing-footer-contact">Contact</a>
              <a href="/terms"   className="hover:text-[#D4AF37] transition-colors" data-testid="landing-footer-terms">Terms</a>
              <a href="/privacy" className="hover:text-[#D4AF37] transition-colors" data-testid="landing-footer-privacy">Privacy</a>
            </nav>
            <div className="text-xs text-[#555]">VelvetGate — Premium Event Access</div>
          </div>
        </footer>

        {/* ── Floating Demo Button ── */}
        <button
          onClick={goDemo}
          className={`fixed bottom-6 right-5 z-50 flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm text-white shadow-lg shadow-black/40 transition-all duration-500 ease-out active:scale-95 ${
            showDemoFab
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
          style={{ background: '#6B1E3A' }}
          data-testid="demo-fab"
          aria-label="Try Guest Experience"
        >
          <Play className="w-3.5 h-3.5 fill-current" />
          Guest Demo
        </button>

      </div>
    </>
  );
}
